// Core fund administration domain types
// Based on backend fund models

import type { LedgerView } from './drawdown';

/** The standard the fund's accounts follow; decides how deal costs post. */
export type AccountingFramework = 'RJ' | 'IFRS' | 'US_GAAP';

export const ACCOUNTING_FRAMEWORK_LABELS: Record<AccountingFramework, string> =
  {
    RJ: 'Dutch GAAP (RJ 615)',
    IFRS: 'IFRS',
    US_GAAP: 'US GAAP',
  };

/** Whether LPs share every investment pro-rata or choose deal by deal. */
export type LpAllocationMode = 'PRO_RATA' | 'DEAL_BY_DEAL';

/** Frameworks that capitalise a closed deal's costs into the investment. */
export const CAPITALISES_CLOSED_DEAL_COSTS: ReadonlySet<AccountingFramework> =
  new Set(['RJ', 'US_GAAP']);

export interface Fund {
  id: string;
  name: string;
  display_name?: string;
  fund_type: 'main' | 'feeder' | 'master' | 'aiv' | 'opportunity';
  legal_structure: 'scs' | 'scsp' | 'sarl' | 'sicav' | 'limited_partnership';
  domicile_country: string;
  base_currency: string;
  minimum_prepayment_pool?: number;
  commitment_amount: number;
  target_size?: number;
  hard_cap?: number;
  first_closing_date?: string;
  final_closing_date?: string;
  fund_term_years?: number;
  extension_periods: number;
  vintage_year: number;
  status: 'setup' | 'fundraising' | 'investing' | 'harvesting' | 'liquidated';
  is_archived: boolean;
  lei_code?: string;
  tax_id?: string;
  coc_number?: string | null;
  vesting_start_date?: string | null;
  vesting_period_years?: number | null;
  vesting_as_of_date?: string | null;
  vesting_end_date?: string | null;
  regulatory_regime?: 'aifmd' | 'ucits' | 'eltif';
  accounting_framework: AccountingFramework;
  lp_allocation_mode: LpAllocationMode;
  parent_fund_id?: string;
  master_fund_id?: string;

  // Basic LPA Terms (stored on fund)
  management_fee_investment_period?: number; // percentage as decimal (e.g., 0.02 for 2%)
  management_fee_post_investment?: number; // percentage as decimal (e.g., 0.015 for 1.5%)
  management_fee_basis_investment?: string; // 'Committed Capital', 'Invested Capital', 'Net Asset Value'
  management_fee_basis_post_investment?: string;
  gp_commitment?: number; // percentage as decimal (e.g., 0.01 for 1%)
  waterfall_type?: string; // 'European', 'American', 'Hybrid'
  distribution_waterfall_type?: {
    id: number;
    code: string;
    name: string;
  } | null;
  hurdle_rate?: number; // percentage as decimal (e.g., 0.08 for 8%)
  gp_catch_up?: number; // percentage as decimal (e.g., 1.0 for 100%)
  carried_interest?: number; // percentage as decimal (e.g., 0.20 for 20%)
  investment_period_years?: number; // duration in years
  fund_costs_cap?: number; // percentage as decimal (e.g., 0.0050 for 0.5%)

  bank_accounts: FundBankAccount[];
  fund_entities: FundEntity[];
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface ApiFundMetrics {
  fund_id: string;
  fund_name: string;
  currency: string;
  view: LedgerView;

  total_commitments: string;
  total_called: string;
  uncalled_capital: string;
  total_contributed: string;
  total_distributed: string;
  nav: string;
  gross_asset_value: string;

  dpi: string | null;
  tvpi: string | null;
  rvpi: string | null;
  gross_moic: string | null;

  net_irr: string | null;

  called_capital_percentage: string | null;
  working_capital: string;

  unrealized_gain_loss: string;
  realized_gain_loss: string;

  invested_cost: string;
  invested_capital: string;
  notes_outstanding: string;
  cash_balance: string;

  concentration_limit: string | null;
  loss_ratio: string | null;
  graduation_rate: string | null;
  initial_capital: string;
  follow_on_capital: string;
  follow_on_ratio: string | null;

  gp_commitment_percentage: string | null;
  gp_commitment_amount: string | null;

  management_fee_rate: string | null;
  management_fee_basis: string | null;
  accrued_management_fees: string;
  paid_management_fees: string;
  unpaid_management_fees: string;
  prepaid_management_fees: string;
  has_active_fee_schedule: boolean;
  fund_expenses: string;
  deal_costs_expensed: string;

  portfolio_company_count: number;
  active_company_count: number;
  exited_company_count: number;
}

export interface FundMetrics {
  fund_id: string;
  fund_name: string;
  currency: string;
  view: LedgerView;

  total_commitments: number;
  total_called: number;
  uncalled_capital: number;
  total_contributed: number;
  total_distributed: number;
  nav: number;
  gross_asset_value: number;

  dpi: number | null;
  tvpi: number | null;
  rvpi: number | null;
  gross_moic: number | null;

  net_irr: number | null;

  called_capital_percentage: number | null;
  working_capital: number;

  unrealized_gain_loss: number;
  realized_gain_loss: number;

  invested_cost: number;
  invested_capital: number;
  notes_outstanding: number;
  cash_balance: number;

  concentration_limit: number | null;
  loss_ratio: number | null;
  graduation_rate: number | null;
  initial_capital: number;
  follow_on_capital: number;
  follow_on_ratio: number | null;

  gp_commitment_percentage: number | null;
  gp_commitment_amount: number | null;

  management_fee_rate: number | null;
  management_fee_basis: string | null;
  accrued_management_fees: number;
  paid_management_fees: number;
  unpaid_management_fees: number;
  prepaid_management_fees: number;
  has_active_fee_schedule: boolean;
  fund_expenses: number;
  deal_costs_expensed: number;

  portfolio_company_count: number;
  active_company_count: number;
  exited_company_count: number;
}

export interface ApiFundMetricsPoint extends ApiFundMetrics {
  as_of: string;
}

export interface FundMetricsPoint extends FundMetrics {
  as_of: string;
}

export interface ApiFundMetricsSeries {
  fund_id: string;
  view: LedgerView;
  currency: string;
  points: ApiFundMetricsPoint[];
}

export interface FundMetricsSeries {
  fund_id: string;
  view: LedgerView;
  currency: string;
  points: FundMetricsPoint[];
}

export type FundFigureKey = {
  [K in keyof FundMetrics]: FundMetrics[K] extends number | null ? K : never;
}[keyof FundMetrics];

export type FigureFormat = 'amount' | 'multiple' | 'percent' | 'count';

export interface FundFigure {
  figure: FundFigureKey;
  label: string;
  format: FigureFormat;
  explanation: string;
}

export interface FundSummary {
  id: string;
  name: string;
  display_name?: string;
  fund_type: string;
  base_currency: string;
  commitment_amount: number;
  vintage_year: number;
  status: string;
  is_archived: boolean;
  parent_fund_id?: string;
  created_at: string;
}

export interface FundOption {
  id: string;
  name: string;
  display_name?: string;
  fund_type: string;
}

export interface FundCreate {
  name: string;
  display_name?: string;
  fund_type: 'main' | 'feeder' | 'master' | 'aiv' | 'opportunity';
  legal_structure: 'scs' | 'scsp' | 'sarl' | 'sicav' | 'limited_partnership';
  domicile_country: string;
  base_currency: string;
  minimum_prepayment?: number;
  commitment_amount: number;
  target_size?: number;
  hard_cap?: number;
  first_closing_date?: string;
  final_closing_date?: string;
  fund_term_years?: number;
  extension_periods?: number;
  vintage_year: number;
  status?: 'setup' | 'fundraising' | 'investing' | 'harvesting' | 'liquidated';
  lei_code?: string;
  tax_id?: string;
  coc_number?: string | null;
  vesting_start_date?: string | null;
  vesting_period_years?: number | null;
  vesting_as_of_date?: string | null;
  regulatory_regime?: 'aifmd' | 'ucits' | 'eltif';
  accounting_framework?: AccountingFramework;
  lp_allocation_mode?: LpAllocationMode;
  parent_fund_id?: string;
  master_fund_id?: string;

  // Basic LPA Terms
  management_fee_investment_period?: number;
  management_fee_post_investment?: number;
  management_fee_basis_investment?: string;
  management_fee_basis_post_investment?: string;
  gp_commitment?: number;
  waterfall_type?: string;
  hurdle_rate?: number;
  gp_catch_up?: number;
  carried_interest?: number;
  investment_period_years?: number;
  fund_costs_cap?: number;
}

export interface FundUpdate {
  name?: string;
  display_name?: string;
  fund_type?: 'main' | 'feeder' | 'master' | 'aiv' | 'opportunity';
  legal_structure?: 'scs' | 'scsp' | 'sarl' | 'sicav' | 'limited_partnership';
  domicile_country?: string;
  base_currency?: string;
  minimum_prepayment_pool?: number;
  commitment_amount?: number;
  target_size?: number;
  hard_cap?: number;
  first_closing_date?: string;
  final_closing_date?: string;
  fund_term_years?: number;
  extension_periods?: number;
  vintage_year?: number;
  status?: 'setup' | 'fundraising' | 'investing' | 'harvesting' | 'liquidated';
  lei_code?: string;
  tax_id?: string;
  coc_number?: string | null;
  regulatory_regime?: 'aifmd' | 'ucits' | 'eltif';
  accounting_framework?: AccountingFramework;
  lp_allocation_mode?: LpAllocationMode;
  parent_fund_id?: string;
  master_fund_id?: string;

  // Basic LPA Terms
  management_fee_investment_period?: number;
  management_fee_post_investment?: number;
  management_fee_basis_investment?: string;
  management_fee_basis_post_investment?: string;
  gp_commitment?: number;
  waterfall_type?: string;
  hurdle_rate?: number;
  gp_catch_up?: number;
  carried_interest?: number;
  investment_period_years?: number;
  fund_costs_cap?: number;
}

export interface FundBankAccount {
  id: string;
  fund_id: string;
  account_name: string;
  account_type: 'operating' | 'escrow' | 'distribution';
  bank_name: string;
  iban?: string;
  swift_bic?: string;
  account_number?: string;
  currency: string;
  is_primary: boolean;
  is_active: boolean;
  opening_date?: string;
  closing_date?: string;
  created_at: string;
  updated_at: string;
}

export interface FundEntity {
  id: string;
  fund_id: string;
  entity_name: string;
  role:
    | 'gp'
    | 'manager'
    | 'master_fund'
    | 'feeder_fund'
    | 'aiv'
    | 'holding_company';
  jurisdiction: string;
  registration_number?: string;
  iban?: string | null;
  created_at: string;
  updated_at: string;
}

export interface FundEmailBatchSummary {
  batch_id: string;
  fund_id: string;
  subject: string;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  status: string;
  created_at: string;
  created_by?: string;
}

export interface FundInvestorEmailResponse {
  id: string;
  investor_id: string;
  contact_id?: string;
  to_email: string;
  to_name?: string;
  status: string;
  error_message?: string;
  sent_at?: string;
  rendered_subject?: string;
  rendered_html_content?: string;
}

// Email specific types
export interface FundEmailBatchDetailResponse extends FundEmailBatchSummary {
  recipients: FundInvestorEmailResponse[];
}

export interface EmailPreviewRequest {
  investorId: string;
  contactId?: string;
  templateId?: string;
  subject?: string;
  htmlContent?: string;
}

export interface EmailPreviewResponse {
  subject: string;
  html: string;
  text: string;
}

interface EmailTarget {
  investor_id: string;
  contact_id?: string;
}

export interface EmailInvestorsRequest {
  subject: string;
  html_content: string;
  text_content?: string;
  targets: EmailTarget[];
}

/**
 * Investor-portal publication controls for a fund.
 *
 * Governs whether and what fund data is exposed to investor-portal users,
 * independent of internal fund status or active investor relationships.
 */
export interface FundPortalPublication {
  id: string;
  fund_id: string;
  is_published: boolean;
  published_as_of_date: string | null;
  publish_fund_details: boolean;
  publish_holdings: boolean;
  publish_portfolio_companies: boolean;
  publish_transactions: boolean;
  publish_dashboard: boolean;
  last_sync_checked_at: string | null;
  last_published_at: string | null;
  has_unreviewed_updates: boolean;
  sync_notes: string | null;
  metrics_cadence: MetricsCadence;
  metrics_auto_release: boolean;
  // Written by the close workflow, not by editing the form.
  metrics_pending_release_period: string | null;
  metrics_last_closed_period: string | null;
  geena_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface FundPortalPublicationUpdate {
  is_published: boolean;
  published_as_of_date: string | null;
  publish_fund_details: boolean;
  publish_holdings: boolean;
  publish_portfolio_companies: boolean;
  publish_transactions: boolean;
  publish_dashboard: boolean;
  metrics_cadence: MetricsCadence;
  metrics_auto_release: boolean;
  geena_enabled: boolean;
}

export type MetricsCadence =
  'monthly' | 'quarterly' | 'semi_annually' | 'annually';

export const METRICS_CADENCE_LABELS: Record<MetricsCadence, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annually: 'Semi-annually',
  annually: 'Annually',
};

/** A fund with a finalized-but-unreleased metrics period, awaiting confirmation. */
export interface MetricsReleaseQueueItem {
  fund_id: string;
  fund_name: string;
  metrics_cadence: MetricsCadence;
  metrics_pending_release_period: string | null;
  /** What investors currently see this fund's metrics up to. */
  published_as_of_date: string | null;
  metrics_auto_release: boolean;
}

/** Body for bulk/individual confirm or revoke of metric releases. */
export interface MetricsReleaseAction {
  fund_ids: string[];
  to_period?: string | null;
}

/** Body for finalizing a fund's period close. */
export interface FinalizeCloseRequest {
  period_end?: string | null;
  cadence?: MetricsCadence | null;
}
